import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { UsersRound, Repeat2, TrendingUp, Coins } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);
const fmtAmt = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtDays = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}d`);

function Kpi({ label, value, sub, icon: Icon, testId, tone = "default" }) {
  const tones = {
    default: "bg-white/70 text-slate-700",
    good: "bg-emerald-100/70 text-emerald-700",
    warn: "bg-amber-100/70 text-amber-700",
  };
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function RetentionPage() {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/retention", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params]);

  const retentionChart = useMemo(() => ([
    { label: "D30", pct: data?.retained_d30_pct || 0, count: data?.retained_d30 || 0, fill: "#22C55E" },
    { label: "D60", pct: data?.retained_d60_pct || 0, count: data?.retained_d60 || 0, fill: "#0A84FF" },
    { label: "D90", pct: data?.retained_d90_pct || 0, count: data?.retained_d90 || 0, fill: "#8B5CF6" },
  ]), [data]);

  const weeklyChart = useMemo(
    () => (data?.weekly_cohorts || []).map((w) => ({
      week: w.week_start.slice(5),
      D30: w.d30_pct,
      D60: w.d60_pct,
      D90: w.d90_pct,
    })),
    [data]
  );

  const { rows: weekRows, thProps: wThProps } = useSortableTable(data?.weekly_cohorts || [], {
    initialKey: "week_start", initialDir: "desc",
    accessors: { week_start: (r) => r.week_start || "" },
  });

  const { rows: campaignRows, thProps: cThProps } = useSortableTable(data?.campaigns || [], {
    initialKey: "cohort_size", initialDir: "desc",
    accessors: { campaign_name: (r) => (r.campaign_name || "").toLowerCase() },
  });

  return (
    <div className="space-y-6" data-testid="retention-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Retention · repeat deposit
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            30 / 60 / 90-day repeat-deposit rate for users converted in the selected window.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="retention" />
      </div>

      <LoadingOverlay loading={loading} testId="retention-loading">
        {/* Header KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Cohort size"
               value={fmt(data?.cohort_size)}
               sub="Unique users converted in window"
               icon={UsersRound}
               testId="retention-kpi-cohort" />
          <Kpi label="D30 retention"
               value={fmtPct(data?.retained_d30_pct)}
               sub={`${fmt(data?.retained_d30)} users`}
               icon={Repeat2}
               tone="good"
               testId="retention-kpi-d30" />
          <Kpi label="D90 retention"
               value={fmtPct(data?.retained_d90_pct)}
               sub={`${fmt(data?.retained_d90)} users repeated in ≤90d`}
               icon={TrendingUp}
               testId="retention-kpi-d90" />
          <Kpi label="Avg $ after conv"
               value={fmtAmt(data?.avg_amount_after_per_user)}
               sub={`Total post-conv: ${fmtAmt(data?.total_amount_after)}`}
               icon={Coins}
               testId="retention-kpi-amt" />
        </div>

        {/* Retention distribution bar chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <div className="glass-card p-5" data-testid="retention-chart">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Retention curve
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={retentionChart} margin={{ top: 30, right: 10, left: 0, bottom: 8 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, k) => (k === "pct" ? [`${Number(v).toFixed(1)}%`, "Retention"] : [v, k])} />
                  <Bar dataKey="pct" radius={[8, 8, 0, 0]}>
                    {retentionChart.map((e) => <Cell key={e.label} fill={e.fill} />)}
                    <LabelList dataKey="pct" position="top" formatter={(v) => `${Number(v).toFixed(1)}%`} style={{ fontSize: 11, fill: "#334155" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Days-to-repeat · avg <span className="font-semibold text-slate-800">{fmtDays(data?.avg_days_to_repeat)}</span>
              · P50 <span className="font-semibold text-slate-800">{fmtDays(data?.p50_days_to_repeat)}</span>
              · P90 <span className="font-semibold text-slate-800">{fmtDays(data?.p90_days_to_repeat)}</span>
            </div>
          </div>

          <div className="glass-card p-5 lg:col-span-2" data-testid="retention-weekly-chart">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Weekly cohort retention (D30 / D60 / D90)
            </div>
            {weeklyChart.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">No cohort data in the selected window.</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyChart} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="D30" stroke="#22C55E" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="D60" stroke="#0A84FF" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="D90" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Weekly cohorts table */}
        <div className="glass-card p-5 mt-8" data-testid="retention-weekly-table">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Weekly cohorts
          </div>
          <div className="overflow-auto max-h-[50vh]">
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-xs uppercase border-b border-slate-100">
                  <SortableTh {...wThProps("week_start", "left")}>Week of</SortableTh>
                  <SortableTh {...wThProps("cohort_size")}>Cohort</SortableTh>
                  <SortableTh {...wThProps("d30")}>D30</SortableTh>
                  <SortableTh {...wThProps("d30_pct")}>D30 %</SortableTh>
                  <SortableTh {...wThProps("d60")}>D60</SortableTh>
                  <SortableTh {...wThProps("d60_pct")}>D60 %</SortableTh>
                  <SortableTh {...wThProps("d90")}>D90</SortableTh>
                  <SortableTh {...wThProps("d90_pct")}>D90 %</SortableTh>
                  <SortableTh {...wThProps("avg_amount_after")}>Avg $ after</SortableTh>
                </tr>
              </thead>
              <tbody>
                {weekRows.map((w) => (
                  <tr key={w.week_start} className="border-b border-slate-50/70">
                    <td className="py-2 px-2 font-medium text-slate-800">{w.week_start}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(w.cohort_size)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(w.d30)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-emerald-700">{fmtPct(w.d30_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(w.d60)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-sky-700">{fmtPct(w.d60_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(w.d90)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-violet-700">{fmtPct(w.d90_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtAmt(w.avg_amount_after)}</td>
                  </tr>
                ))}
                {weekRows.length === 0 && (
                  <tr><td colSpan={9} className="py-4 text-center text-slate-500">No cohorts.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Per-campaign retention */}
        <div className="glass-card p-5 mt-8" data-testid="retention-campaigns">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Retention by campaign (top 30)
          </div>
          <div className="overflow-auto max-h-[50vh]">
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-xs uppercase border-b border-slate-100">
                  <SortableTh {...cThProps("campaign_name", "left")}>Campaign</SortableTh>
                  <SortableTh {...cThProps("cohort_size")}>Cohort</SortableTh>
                  <SortableTh {...cThProps("d30_pct")}>D30 %</SortableTh>
                  <SortableTh {...cThProps("d60_pct")}>D60 %</SortableTh>
                  <SortableTh {...cThProps("d90_pct")}>D90 %</SortableTh>
                  <SortableTh {...cThProps("avg_amount_after")}>Avg $ after</SortableTh>
                </tr>
              </thead>
              <tbody>
                {campaignRows.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-slate-50/70">
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-800 truncate max-w-[240px]">{c.campaign_name}</div>
                      <div className="text-[10px] text-slate-500">{c.brand_name}</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(c.cohort_size)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-1.5 py-0.5 rounded tabular-nums ${c.d30_pct >= 25 ? "bg-emerald-50 text-emerald-700" : c.d30_pct >= 10 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-600"}`}>
                        {fmtPct(c.d30_pct)}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(c.d60_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(c.d90_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtAmt(c.avg_amount_after)}</td>
                  </tr>
                ))}
                {campaignRows.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-slate-500">No campaigns.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </LoadingOverlay>
    </div>
  );
}
